import { Button, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ReactNode, useMemo, useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { BaseEntityDefaultValues } from "@/common/ts/entity";
import { BaseEntityDto } from "@/core/api/generated/v1";

import { BaseEntitiesSearchAutocompleteInheritableProps } from "./BaseEntitiesSearchAutocomplete";
import { BaseEntitySearchAutocompleteInheritableProps } from "./BaseEntitySearchAutocomplete";
import { EntityDefaultValuesMatchStatusDisplay } from "./EntityDefaultValuesMatchStatusDisplay";

export const AutocompleteType = {
  Single: "Single",
  Multiple: "Multiple",
} as const;
export type AutocompleteType = (typeof AutocompleteType)[keyof typeof AutocompleteType];

export type CreateUpdateFormPlacement = "modal" | "page";

type SingleAutocompleteOnChange<TEntity extends BaseEntityDto> =
  BaseEntitySearchAutocompleteInheritableProps<TEntity>["onChange"];
type MultipleAutocompleteOnChange<TEntity extends BaseEntityDto> =
  BaseEntitiesSearchAutocompleteInheritableProps<TEntity>["onChange"];

type AbstractAutocompleteProps<TEntity extends BaseEntityDto> = Partial<
  Pick<BaseEntitySearchAutocompleteInheritableProps<TEntity>, "entityId" | "entity">
> &
  Partial<
    Pick<BaseEntitiesSearchAutocompleteInheritableProps<TEntity>, "entityIds" | "entities">
  > & {
    onChange?: SingleAutocompleteOnChange<TEntity> | MultipleAutocompleteOnChange<TEntity>;
  };

interface RenderParams<
  TEntity extends BaseEntityDto,
  TDefaultValues extends Nil<BaseEntityDefaultValues>,
> {
  defaultValues: Nil<TDefaultValues>;
  isCreate: boolean;
  setDefaultValues: (newValues: TDefaultValues | undefined) => void;
  setIsCreate: (newValue: boolean) => void;
  onCreateStart: (() => void) | undefined;
  onCreate: (newValue: TEntity) => void;
}

interface RenderAutocompleteParams<
  TEntity extends BaseEntityDto,
  TDefaultValues extends Nil<BaseEntityDefaultValues>,
  AutocompleteProps extends AbstractAutocompleteProps<TEntity>,
  CreateUpdateProps,
> extends RenderParams<TEntity, TDefaultValues> {
  autocompleteProps: AutocompleteProps;
}

interface RenderCreateUpdateParams<
  TEntity extends BaseEntityDto,
  TDefaultValues extends Nil<BaseEntityDefaultValues>,
  AutocompleteProps extends AbstractAutocompleteProps<TEntity>,
  CreateUpdateProps,
> extends RenderParams<TEntity, TDefaultValues> {
  autocompleteProps: AutocompleteProps;
  createUpdateProps: Nil<CreateUpdateProps>;
}

interface RenderCreateUpdateModalParams<
  TEntity extends BaseEntityDto,
  TDefaultValues extends Nil<BaseEntityDefaultValues>,
  AutocompleteProps extends AbstractAutocompleteProps<TEntity>,
  CreateUpdateProps,
> extends RenderParams<TEntity, TDefaultValues> {
  autocompleteProps: AutocompleteProps;
  createUpdateProps: Nil<CreateUpdateProps>;
}

export interface BaseEntityAutocompleteOrCreateProps<
  TEntity extends BaseEntityDto,
  TDefaultValues extends Nil<BaseEntityDefaultValues>,
  AutocompleteProps extends AbstractAutocompleteProps<TEntity>,
  CreateUpdateProps,
> {
  autocompleteType: AutocompleteType;
  autocompleteProps: AutocompleteProps;
  createUpdateProps?: Nil<CreateUpdateProps>;
  createFormPlacement?: CreateUpdateFormPlacement;
  getDefaultValues: (params: { createUpdateProps: Nil<CreateUpdateProps> }) => Nil<TDefaultValues>;
  renderAutocomplete: (
    params: RenderAutocompleteParams<TEntity, TDefaultValues, AutocompleteProps, CreateUpdateProps>,
  ) => ReactNode;
  renderCreateUpdate: (
    params: RenderCreateUpdateParams<TEntity, TDefaultValues, AutocompleteProps, CreateUpdateProps>,
  ) => ReactNode;
  renderCreateUpdateModal: (
    params: RenderCreateUpdateModalParams<
      TEntity,
      TDefaultValues,
      AutocompleteProps,
      CreateUpdateProps
    >,
  ) => ReactNode;
  onCreateStart?: () => void;
  /** Set to handle creation separately from Autocomplete.onChange.
   * If not set, Autocomplete.onChange will be called.
   * Pass noop to do nothing on create.
   * */
  onCreate: ((newValue: TEntity) => void) | undefined;
}

export interface BaseEntityAutocompleteOrCreateInheritableProps<
  TEntity extends BaseEntityDto,
  TDefaultValues extends Nil<BaseEntityDefaultValues>,
  AutocompleteProps extends AbstractAutocompleteProps<TEntity>,
  CreateUpdateProps,
> {
  autocompleteProps: AutocompleteProps;
  createUpdateProps?: CreateUpdateProps | null;
  createFormPlacement?: CreateUpdateFormPlacement;
  onCreateStart?: () => void;
  /** Set to handle creation separately from Autocomplete.onChange.
   * If not set, Autocomplete.onChange will be called.
   * Pass noop to do nothing on create.
   * */
  onCreate: ((newValue: TEntity) => void) | undefined;
}

/** EntityAutocomplete + EntityCreateUpdate */
export default function BaseEntityAutocompleteOrCreate<
  TEntity extends BaseEntityDto,
  TDefaultValues extends Nil<BaseEntityDefaultValues>,
  AutocompleteProps extends AbstractAutocompleteProps<TEntity>,
  CreateUpdateProps,
>({
  autocompleteType,
  autocompleteProps,
  createUpdateProps,
  createFormPlacement = "modal",
  getDefaultValues,
  renderAutocomplete,
  renderCreateUpdate,
  renderCreateUpdateModal,
  onCreateStart,
  onCreate,
}: BaseEntityAutocompleteOrCreateProps<
  TEntity,
  TDefaultValues,
  AutocompleteProps,
  CreateUpdateProps
>) {
  const [isCreate, setIsCreate] = useState(false);
  const [_defaultValues, _setDefaultValues] = useState<Nil<TDefaultValues>>(undefined);

  const defaultValues = getDefaultValues({ createUpdateProps });
  const defaultValuesComputed = useMemo<Nil<TDefaultValues>>(
    () =>
      defaultValues || _defaultValues
        ? ({ ...defaultValues, ..._defaultValues } as Nil<TDefaultValues>)
        : undefined,
    [defaultValues, _defaultValues],
  );

  const setDefaultValuesComputed = (newValues: Nil<TDefaultValues>) => {
    _setDefaultValues(newValues);
  };

  const onCreateComputed = (newValue: TEntity) => {
    if (onCreate) {
      onCreate(newValue);
    } else {
      if (autocompleteType === AutocompleteType.Single) {
        const autocompleteOnChange =
          autocompleteProps.onChange as SingleAutocompleteOnChange<TEntity>;
        autocompleteOnChange && autocompleteOnChange(newValue);
      } else if (autocompleteType === AutocompleteType.Multiple) {
        const autocompleteOnChange =
          autocompleteProps.onChange as MultipleAutocompleteOnChange<TEntity>;
        autocompleteOnChange && autocompleteOnChange(newValue ? [newValue] : undefined);
      }
    }
  };

  const renderParams: RenderParams<TEntity, TDefaultValues> = {
    defaultValues: defaultValuesComputed,
    isCreate,
    setDefaultValues: setDefaultValuesComputed,
    setIsCreate,
    onCreateStart,
    onCreate: onCreateComputed,
  };

  const entityId = autocompleteProps.entityId || autocompleteProps.entity?.id;
  const defaultValuesMatchStatus = useMemo(
    () =>
      EntityHelper.computeDefaultValuesMatchStatus({
        defaultValues: defaultValuesComputed,
        selectedEntityId: entityId,
      }),
    [defaultValuesComputed, entityId],
  );

  return (
    <Stack spacing={2}>
      {/* 1. Search */}
      {!isCreate && (
        <Box>
          {renderAutocomplete({
            ...renderParams,
            autocompleteProps,
          })}

          {/* Extracted data status */}
          {defaultValuesMatchStatus && (
            <EntityDefaultValuesMatchStatusDisplay
              status={defaultValuesMatchStatus}
              isAllowCreate
              onCreate={() => {
                setIsCreate(true);
              }}
            />
          )}
        </Box>
      )}

      {/* 2. Create */}
      {isCreate && (
        <Box>
          {isCreate && (
            <>
              {createFormPlacement === "modal" &&
                renderCreateUpdateModal({
                  ...renderParams,
                  autocompleteProps,
                  createUpdateProps,
                })}

              {createFormPlacement === "page" && (
                <Stack spacing={1}>
                  <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
                    <Button
                      variant='text'
                      color='text'
                      size='small'
                      startIcon={<AppIcon of='goBack' />}
                      onClick={() => setIsCreate(false)}
                    >
                      Back to search
                    </Button>

                    <Typography component='div' variant='subtitle1'>
                      Create new
                    </Typography>
                  </Stack>

                  {renderCreateUpdate({
                    ...renderParams,
                    autocompleteProps,
                    createUpdateProps,
                  })}
                </Stack>
              )}
            </>
          )}
        </Box>
      )}
    </Stack>
  );
}
